import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class CsvExportService {

    constructor() {}

    /**
     * Export data to CSV file
     * @param data Array of objects to export
     * @param filename Name of the file to download
     * @param headers Optional custom headers mapping
     */
    public exportToCsv<T>(data: T[], filename: string, headers?: { [key: string]: string }): void {
        if (!data || data.length === 0) {
            console.warn('No data to export');
            return;
        }

        // Get headers from first object or use provided headers
        const csvHeaders = headers || this.getHeadersFromData(data[0]);
        
        // Convert data to CSV format
        const csvContent = this.convertToCsv(data, csvHeaders);
        
        // Create and download file
        this.downloadFile(csvContent, filename);
    }

    /**
     * Get headers from the first data object
     */
    private getHeadersFromData<T>(data: T): { [key: string]: string } {
        const keys = Object.keys(data);
        const headers: { [key: string]: string } = {};
        keys.forEach(key => {
            headers[key] = key;
        });
        return headers;
    }

    /**
     * Convert data array to CSV string
     */
    private convertToCsv<T>(data: T[], headers: { [key: string]: string }): string {
        const csvRows: string[] = [];
        
        // Add header row
        const headerRow = Object.values(headers).join(',');
        csvRows.push(headerRow);
        
        // Add data rows
        data.forEach(item => {
            const values = Object.keys(headers).map(key => {
                const value = this.getNestedValue(item, key);
                return this.escapeCsvValue(value);
            });
            csvRows.push(values.join(','));
        });
        
        return csvRows.join('\n');
    }

    /**
     * Get nested value from object using dot notation
     */
    private getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((current, key) => current?.[key], obj) ?? '';
    }

    /**
     * Escape CSV value (handle commas, quotes, newlines)
     */
    private escapeCsvValue(value: any): string {
        if (value === null || value === undefined) {
            return '';
        }
        
        const stringValue = String(value);
        
        // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }
        
        return stringValue;
    }

    /**
     * Create and trigger download of CSV file
     */
    private downloadFile(csvContent: string, filename: string): void {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    }
}
